import dynamicField from "$routes/_lib/dynamic-form-fields/dynamic-field-hoc";
import type { FieldMap } from "$routes/_lib/dynamic-form-fields/dynamic-form-fields-context";
import DynamicResult, {
  DefaultDynamicResultComponent,
  DynamicResultFieldsContext,
  type DynamicResultProps,
} from "$routes/patient/[:patientId]/_lib/investigations/dynamic-investigation-results/dynamic-result";
import InvestigationResults from "$routes/patient/[:patientId]/_lib/investigations/dynamic-investigation-results/investigation-results";
import UnitResult from "$routes/patient/[:patientId]/_lib/investigations/dynamic-investigation-results/unit-result";
import type { Property } from "$routes/patient/[:patientId]/_lib/investigations/property-type";
import {
  L10N,
  translateI18nObject,
} from "$routes/patient/[:patientId]/_lib/translation-util";
import React from "react";

const withValue = (property: Property) => {
  if (property.value && property.value !== property.default) {
    return true;
  }

  if (property.orderedProperties) {
    return property.orderedProperties.some((childProperty) =>
      withValue(childProperty),
    );
  }

  return false;
};

const DynamicInvestigationResults = ({
  property: { orderedProperties },
  print,
}: DynamicResultProps) => {
  const items = orderedProperties.reduce(
    (result, currentProperty: Property, i: number) => {
      const translatedCurrentProperty = translateI18nObject(
        currentProperty,
        L10N.EN,
      );
      if (withValue(translatedCurrentProperty)) {
        // Safe to use array index as key as the order does not change
        result.push([
          translatedCurrentProperty,
          <DynamicResult property={translatedCurrentProperty} key={i} />,
        ]);
      }

      return result;
    },
    [],
  ) as [Property, React.ReactElement][];

  return <InvestigationResults items={items} print={print} />;
};

const InvestigationActivityResultsRaw = (
  props: DynamicResultProps & { fieldMap?: FieldMap<DynamicResultProps> },
): React.ReactElement => {
  const { fieldMap, ...restProps } = props;
  return (
    <DynamicResultFieldsContext.Provider value={{ fieldMap }}>
      <DynamicInvestigationResults {...restProps} />
    </DynamicResultFieldsContext.Provider>
  );
};

/**
 * Maps to custom components and types of the property
 */
const defaultFieldMap = {
  BmiInput: dynamicField(UnitResult),
  UnitInput: dynamicField(UnitResult),
  TextArea: dynamicField(DefaultDynamicResultComponent),
  object: dynamicField(DynamicInvestigationResults),
  TEWSScoreInput: dynamicField(UnitResult),
};

DynamicInvestigationResults.defaultProps = {
  print: false,
  property: {
    orderedProperties: [],
  },
};
InvestigationActivityResultsRaw.defaultProps = {
  fieldMap: defaultFieldMap,

  print: false,
};

export const InvestigationActivityResults = React.memo(
  InvestigationActivityResultsRaw,
);
